import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchTitleImportStatus, } from "../api/titleImportService";
import { isTerminalImportStatus, nextImportPollInterval, } from "../utils/importErrors";
var POLL_INTERVAL = 2000;
var PROGRESS_CACHE_PREFIX = "title-import-progress-";
/**
 * Consecutive status-poll failures tolerated inside a single poll cycle before
 * the query is marked errored. Kept high so a transient timeout or a dropped
 * connection never surfaces as a terminal error — the import runs on the backend
 * and the poll self-heals on the next tick regardless.
 */
var STATUS_POLL_RETRIES = 5;
var getCachedProgress = function (fileName) {
    try {
        var cached = sessionStorage.getItem(PROGRESS_CACHE_PREFIX + fileName);
        return cached ? JSON.parse(cached) : null;
    }
    catch (_a) {
        return null;
    }
};
var cacheProgress = function (fileName, progress) {
    try {
        sessionStorage.setItem(PROGRESS_CACHE_PREFIX + fileName, JSON.stringify(progress));
    }
    catch (_a) {
        /* empty */
    }
};
export var useTitleImportProgress = function (fileName) {
    var kyInstance = useOkapiKy();
    var cached = fileName ? getCachedProgress(fileName) : null;
    var progress = useQuery(["title-import-progress", fileName], function () { return fetchTitleImportStatus(kyInstance, fileName); }, {
        enabled: !!fileName && !(cached && isTerminalImportStatus(cached.status)),
        initialData: cached !== null && cached !== void 0 ? cached : undefined,
        keepPreviousData: true,
        // Tolerate several consecutive poll failures; a transient timeout must not
        // end tracking. Cap the backoff so recovery stays within a poll interval.
        retry: STATUS_POLL_RETRIES,
        retryDelay: function (attempt) { return Math.min(1000 * Math.pow(2, attempt), POLL_INTERVAL); },
        // Only a real terminal status (COMPLETED/FAILED) stops the poll. A fetch
        // error leaves `data` as the last known value (or undefined on first load),
        // neither of which is terminal, so polling keeps running and self-heals.
        refetchInterval: function (data) { return nextImportPollInterval(data, POLL_INTERVAL); },
        // Keep polling even when the browser tab is in the background, so a long
        // import still reaches its terminal state without a manual refresh.
        refetchIntervalInBackground: true,
        onSuccess: function (data) {
            cacheProgress(fileName, data);
        },
    }).data;
    return progress !== null && progress !== void 0 ? progress : null;
};
